import type { QueryClient } from '@tanstack/react-query';

/**
 * 알림 딥링크가 연 창의 좌표를 무효화하는 **정본 맵**.
 *
 * ## 무엇을 고치는가
 *
 * 알림을 눌러 창이 열려도 그 창은 데이터를 다시 불러오지 않았다 — 전역 `staleTime` 5분과
 * `refetchOnWindowFocus:false`(`shared/api/queryClient.ts`)가 겹쳐 **재조회가 아예 일어나지
 * 않기** 때문이다. 그래서 알림이 알린 변화가 정작 화면에는 없는 구간이 생겼다.
 * 창마다 새로고침 버튼을 다는 대신, 착지 시점에 그 창의 좌표만 무효화한다.
 *
 * ## 왜 `import()` 인가
 *
 * 이 파일은 `app/providers` 가 모듈 스코프에서 등록하므로 **초기 청크와 함께 로드된다**.
 * 키 팩토리를 정적으로 끌면 그 배럴이 통째로 딸려온다 — `@/entities/attendance` 는
 * `TeamAttendanceTable` · `AuditLogSection` 같은 UI 까지 재수출한다. `prefetchByView` 가
 * 같은 이유로 동적 import 를 쓴다. 다만 저쪽과 달리 **키는 팩토리에서 가져온다**(리터럴
 * 금지 — `shared/api/__tests__/queryKeyLiteralGate.test.ts`).
 *
 * ## 왜 좁히지 않는가
 *
 * `invalidateQueries` 기본 `refetchType` 은 `'active'` 라 **닫힌 화면은 재조회 0회**다.
 * 열린 좌표를 넉넉히 선언하는 비용은 열린 화면 몇 개가 더 도는 정도이고, 좁히다 놓친
 * 파생 뷰가 조용히 stale 되는 쪽이 훨씬 비싸다(`defineInvalidation` 도크스트링과 같은 판단).
 *
 * `view` 가 `AppView` 가 아니라 `string` 인 이유는 `shared/lib/deepLinkQueryInvalidation` 의
 * 도크스트링 참고 — 푸시 URL 경로의 값이 검증되지 않은 쿼리 문자열이다. 모르는 값은 no-op.
 *
 * 계약 회귀 가드: `__tests__/deepLinkQueryScopes.dom.test.ts`.
 */
export async function invalidateViewQueries(queryClient: QueryClient, view: string): Promise<void> {
  switch (view) {
    // 퀘스트 3창은 같은 퀘스트 도메인을 본다(dev-quests 는 제품 축으로 좁힌 같은 목록).
    // '오늘' 창도 수락 퀘스트·태스크·질문 스레드를 같은 키로 읽으므로 함께 묶는다
    // (QUEST_QUESTION_CREATED 의 착지 창이 'today' 다).
    case 'quests':
    case 'world-quests':
    case 'dev-quests':
    case 'today': {
      const [{ questKeys }, { questTaskKeys, questQuestionKeys }, checkpoint] = await Promise.all([
        import('@/shared/queryKeys'),
        import('@/entities/quest-task'),
        import('@/entities/checkpoint'),
      ]);
      void queryClient.invalidateQueries({ queryKey: questKeys.all });
      void queryClient.invalidateQueries({ queryKey: questTaskKeys.all });
      void queryClient.invalidateQueries({ queryKey: questQuestionKeys.all });
      // 배지·게이지가 batch 키로 파생되므로 detail 만 지우면 목록 숫자가 남는다(PR #485 선례).
      checkpoint.invalidateCheckpointScopes(queryClient, ['detail', 'batch', 'teamPendingReviews']);
      // 증빙(`proofs`)은 뺐다 — 소비 화면이 0곳인 죽은 키다(`entities/proof/model/proofKeys.ts`).
      return;
    }

    // 본인 근태 창 — 근태·정정·휴가·비근무일 결과 알림이 전부 여기로 착지한다.
    case 'attendance': {
      const [attendance, { leaveKeys }, { offDayWorkKeys }] = await Promise.all([
        import('@/entities/attendance'),
        import('@/entities/leave'),
        import('@/features/off-day-work'),
      ]);
      attendance.invalidateAttendanceScopes(queryClient, ['me', 'calendar', 'weeklySummary']);
      attendance.invalidateCorrectionScopes(queryClient, ['my', 'segment']);
      void queryClient.invalidateQueries({ queryKey: leaveKeys.meAll });
      void queryClient.invalidateQueries({ queryKey: offDayWorkKeys.all });
      return;
    }

    // 근태 운영 창 — 팀장 근태 생성 알림이 착지한다. 본인 캐시는 건드리지 않는다.
    case 'admin-attendance': {
      const attendance = await import('@/entities/attendance');
      attendance.invalidateAttendanceScopes(queryClient, ['admin']);
      attendance.invalidateCorrectionScopes(queryClient, ['admin']);
      return;
    }

    // 팀 운영 창 — 착지 지점이 전부 결재함(휴가·정정·비근무·정성 체크포인트)이다.
    // 주간보고서 탭(WEEKLY_REPORT_NOT_WRITTEN)은 뺐다: "아직 안 썼다"는 독촉이라 서버
    // 데이터가 바뀐 게 아니고, 그 키는 팩토리가 아니라 조회 훅 안의 지역 함수다.
    case 'team-settings': {
      const [attendance, { leaveKeys }, { offDayWorkKeys }, checkpoint, { teamKeys }] =
        await Promise.all([
          import('@/entities/attendance'),
          import('@/entities/leave'),
          import('@/features/off-day-work'),
          import('@/entities/checkpoint'),
          import('@/entities/team'),
        ]);
      attendance.invalidateCorrectionScopes(queryClient, ['admin', 'segment']);
      void queryClient.invalidateQueries({ queryKey: leaveKeys.adminAll });
      void queryClient.invalidateQueries({ queryKey: offDayWorkKeys.all });
      checkpoint.invalidateCheckpointScopes(queryClient, ['teamPendingReviews']);
      // 배당 확정 알림(DIVIDEND_CONFIRM_*)이 budget 탭으로 착지한다 — 확정 여부·풀 금액이
      // 그 사이 움직였을 수 있으므로 함께 무효화한다(정산 중에는 늦게 승인된 증빙으로 풀이 변한다).
      void queryClient.invalidateQueries({ queryKey: teamKeys.budgetAllocationAll });
      return;
    }

    // 대표 콘솔 — 정산 시작 안내·마감 가능 알림이 착지한다. 마감 조건 판정은 어그리게이트가
    // 아직 싣지 않지만(체크리스트 카드는 형제 퀘스트 소관) 큐·예산 카드가 같은 창에 있고
    // 그 값들이 정산 중에 움직이므로 착지 시점에 다시 읽는다.
    case 'ceo-console': {
      const { CEO_DASHBOARD_KEYS } = await import('@/features/ceo-console');
      void queryClient.invalidateQueries({ queryKey: CEO_DASHBOARD_KEYS.all });
      return;
    }

    // 업무일지 창 — 미제출 독촉(본인)과 제출 알림(팀장)이 서로 다른 루트를 본다.
    case 'work-log': {
      const { invalidateWorkLogScopes, workLogKeys } = await import('@/entities/work-log');
      invalidateWorkLogScopes(queryClient, ['list', 'today', 'count', 'lifetimeStats']);
      void queryClient.invalidateQueries({ queryKey: workLogKeys.teamAll });
      return;
    }

    // 리더보드 창 — 순위 변동·포인트 정산 알림이 착지한다. 정산은 잔액·내역도 함께 바꾼다.
    case 'leaderboard': {
      const { leaderboardKeys, pointBalanceKeys, pointHistoryKeys } =
        await import('@/shared/queryKeys');
      void queryClient.invalidateQueries({ queryKey: leaderboardKeys.all });
      void queryClient.invalidateQueries({ queryKey: pointBalanceKeys.all });
      void queryClient.invalidateQueries({ queryKey: pointHistoryKeys.all });
      return;
    }

    case 'bug-reports': {
      const { bugReportKeys } = await import('@/entities/bug-report');
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
      return;
    }

    // 라운지 창 — 동아리(피드·투표)와 회의실 예약이 한 창을 나눠 쓴다.
    case 'lounge': {
      const [{ clubQueryKeys }, { roomReservationKeys }] = await Promise.all([
        import('@/features/club'),
        import('@/features/room-reservation'),
      ]);
      void queryClient.invalidateQueries({ queryKey: clubQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: roomReservationKeys.all });
      return;
    }

    // 무효화 대상이 없는 뷰 — 조용히 no-op.
    //
    // 'chat'(MENTION·REPLY)은 **의도적으로 뺐다**: 채팅은 소켓이 메시지를 실시간으로 캐시에
    // 밀어 넣어 애초에 stale 갭이 없고, 무한 스크롤 목록을 무효화하면 읽던 위치가 흔들린다.
    // 나머지는 getDeepLink 가 착지시키지 않는 창이다.
    default:
      return;
  }
}
